/**
 * LoanTermsForm - Step 2 of CD Creation Wizard
 *
 * Collects all loan information including:
 * - Basic loan details (amount, rate, term, purpose, type)
 * - Prepayment penalty settings
 * - Balloon payment settings
 * - Rate adjustment settings (for ARMs)
 * - Escrow configuration
 */

import { format, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { EscrowedItem, LoanPurpose, LoanTerms, LoanType } from '../../types';
import { calculateMonthlyPayment, formatCurrency, normalizeDateString } from '../../utils';
import { CurrencyInput, PercentageInput } from './shared';

interface LoanTermsFormProps {
  loanTerms?: Partial<LoanTerms> | null;
  onNext: (data: Partial<LoanTerms>) => void;
  onBack: () => void;
}

/** Loan term options in months */
const LOAN_TERM_OPTIONS = [
  { value: '180', label: '15 Years (180 months)' },
  { value: '240', label: '20 Years (240 months)' },
  { value: '360', label: '30 Years (360 months)' },
];

/** Loan purpose options */
const LOAN_PURPOSE_OPTIONS: { value: LoanPurpose; label: string }[] = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'construction', label: 'Construction' },
  { value: 'other', label: 'Other' },
];

/** Loan type options */
const LOAN_TYPE_OPTIONS: { value: LoanType; label: string }[] = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'usda', label: 'USDA' },
  { value: 'other', label: 'Other' },
];

/** Escrow item options */
const ESCROW_ITEM_OPTIONS: { value: EscrowedItem; label: string }[] = [
  { value: 'property_taxes', label: 'Property Taxes' },
  { value: 'homeowners_insurance', label: "Homeowner's Insurance" },
  { value: 'other', label: 'Other (PMI, HOA, etc.)' },
];

/** Common loan products */
const LOAN_PRODUCT_SUGGESTIONS = ['Fixed Rate', '5/1 ARM', '7/1 ARM', '10/1 ARM', 'Interest Only'];

interface FormData {
  loanAmount: number | null;
  interestRate: number | null;
  loanTermMonths: string;
  loanPurpose: LoanPurpose;
  loanType: LoanType;
  loanProduct: string;
  loanId: string;
  mic: string;
  prepaymentPenalty: boolean;
  prepaymentPenaltyDetails: string;
  balloonPayment: boolean;
  balloonPaymentAmount: number | null;
  balloonPaymentDueDate: string;
  canIncreaseAfterClosing: boolean;
  firstChangeDate: string;
  changeFrequency: string;
  maxRateIncrease: number | null;
  maxRateDecrease: number | null;
  rateAdjustmentCap: number | null;
  hasEscrow: boolean;
  escrowedItems: EscrowedItem[];
  estimatedEscrowPayment: number | null;
}

const defaultFormData: FormData = {
  loanAmount: null,
  interestRate: null,
  loanTermMonths: '360',
  loanPurpose: 'purchase',
  loanType: 'conventional',
  loanProduct: 'Fixed Rate',
  loanId: '',
  mic: '',
  prepaymentPenalty: false,
  prepaymentPenaltyDetails: '',
  balloonPayment: false,
  balloonPaymentAmount: null,
  balloonPaymentDueDate: '',
  canIncreaseAfterClosing: false,
  firstChangeDate: '',
  changeFrequency: '',
  maxRateIncrease: null,
  maxRateDecrease: null,
  rateAdjustmentCap: null,
  hasEscrow: true,
  escrowedItems: ['property_taxes', 'homeowners_insurance'],
  estimatedEscrowPayment: null,
};

const LoanTermsForm: React.FC<LoanTermsFormProps> = ({ loanTerms, onNext, onBack }) => {
  const [formData, setFormData] = useState<FormData>(() => ({
    ...defaultFormData,
    ...(loanTerms && {
      loanAmount: loanTerms.loan_amount ?? null,
      interestRate: loanTerms.interest_rate ?? null,
      loanTermMonths: loanTerms.loan_term_months?.toString() ?? '360',
      loanPurpose: loanTerms.loan_purpose ?? 'purchase',
      loanType: loanTerms.loan_type ?? 'conventional',
      loanProduct: loanTerms.loan_product ?? 'Fixed Rate',
      loanId: loanTerms.loan_id ?? '',
      mic: loanTerms.mic ?? '',
      prepaymentPenalty: loanTerms.prepayment_penalty ?? false,
      prepaymentPenaltyDetails: loanTerms.prepayment_penalty_details ?? '',
      balloonPayment: loanTerms.balloon_payment ?? false,
      balloonPaymentAmount: loanTerms.balloon_payment_amount ?? null,
      balloonPaymentDueDate: normalizeDateString(loanTerms.balloon_payment_due_date),
      canIncreaseAfterClosing: loanTerms.can_increase_after_closing ?? false,
      firstChangeDate: normalizeDateString(loanTerms.first_change_date),
      changeFrequency: loanTerms.change_frequency ?? '',
      maxRateIncrease: loanTerms.max_rate_increase ?? null,
      maxRateDecrease: loanTerms.max_rate_decrease ?? null,
      rateAdjustmentCap: loanTerms.rate_adjustment_cap ?? null,
      hasEscrow: loanTerms.has_escrow ?? true,
      escrowedItems: loanTerms.escrowed_items ?? ['property_taxes', 'homeowners_insurance'],
      estimatedEscrowPayment: loanTerms.estimated_escrow_payment ?? null,
    }),
  }));

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [balloonDueDateOpen, setBalloonDueDateOpen] = useState(false);
  const [firstChangeDateOpen, setFirstChangeDateOpen] = useState(false);

  // Calculate monthly P&I whenever loan details change
  const monthlyPrincipalAndInterest = React.useMemo(() => {
    if (formData.loanAmount && formData.interestRate && formData.loanTermMonths) {
      return calculateMonthlyPayment(
        formData.loanAmount,
        formData.interestRate,
        Number.parseInt(formData.loanTermMonths, 10),
      );
    }
    return 0;
  }, [formData.loanAmount, formData.interestRate, formData.loanTermMonths]);

  // Calculate total monthly payment
  const estimatedTotalMonthlyPayment = React.useMemo(() => {
    return monthlyPrincipalAndInterest + (formData.estimatedEscrowPayment ?? 0);
  }, [monthlyPrincipalAndInterest, formData.estimatedEscrowPayment]);

  const updateField = useCallback(
    <K extends keyof FormData>(field: K, value: FormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors],
  );

  const toggleEscrowItem = useCallback((item: EscrowedItem) => {
    setFormData((prev) => {
      const items = prev.escrowedItems.includes(item)
        ? prev.escrowedItems.filter((i) => i !== item)
        : [...prev.escrowedItems, item];
      return { ...prev, escrowedItems: items };
    });
  }, []);

  // Clear conditional fields when their parent is toggled off
  useEffect(() => {
    if (!formData.prepaymentPenalty) {
      setFormData((prev) => ({ ...prev, prepaymentPenaltyDetails: '' }));
    }
  }, [formData.prepaymentPenalty]);

  useEffect(() => {
    if (!formData.balloonPayment) {
      setFormData((prev) => ({
        ...prev,
        balloonPaymentAmount: null,
        balloonPaymentDueDate: '',
      }));
    }
  }, [formData.balloonPayment]);

  useEffect(() => {
    if (!formData.canIncreaseAfterClosing) {
      setFormData((prev) => ({
        ...prev,
        firstChangeDate: '',
        changeFrequency: '',
        maxRateIncrease: null,
        maxRateDecrease: null,
        rateAdjustmentCap: null,
      }));
    }
  }, [formData.canIncreaseAfterClosing]);

  useEffect(() => {
    if (!formData.hasEscrow) {
      setFormData((prev) => ({
        ...prev,
        escrowedItems: [],
        estimatedEscrowPayment: null,
      }));
    }
  }, [formData.hasEscrow]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.loanAmount || formData.loanAmount <= 0) {
      newErrors.loanAmount = 'Loan amount is required and must be greater than 0';
    }

    if (formData.interestRate === null || formData.interestRate < 0) {
      newErrors.interestRate = 'Interest rate is required';
    } else if (formData.interestRate > 30) {
      newErrors.interestRate = 'Interest rate seems unusually high';
    }

    if (!formData.loanTermMonths) {
      newErrors.loanTermMonths = 'Loan term is required';
    }

    if (!formData.loanProduct.trim()) {
      newErrors.loanProduct = 'Loan product is required';
    }

    // Conditional validation
    if (formData.prepaymentPenalty && !formData.prepaymentPenaltyDetails.trim()) {
      newErrors.prepaymentPenaltyDetails = 'Please describe the prepayment penalty terms';
    }

    if (formData.balloonPayment) {
      if (!formData.balloonPaymentAmount || formData.balloonPaymentAmount <= 0) {
        newErrors.balloonPaymentAmount = 'Balloon payment amount is required';
      }
      if (!formData.balloonPaymentDueDate) {
        newErrors.balloonPaymentDueDate = 'Balloon payment due date is required';
      }
    }

    if (formData.canIncreaseAfterClosing) {
      if (!formData.firstChangeDate) {
        newErrors.firstChangeDate = 'First change date is required for adjustable rates';
      }
      if (!formData.changeFrequency.trim()) {
        newErrors.changeFrequency = 'Change frequency is required';
      }
    }

    if (formData.hasEscrow && formData.escrowedItems.length === 0) {
      newErrors.escrowedItems = 'Please select at least one escrowed item';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      const data: Partial<LoanTerms> = {
        loan_amount: formData.loanAmount!,
        interest_rate: formData.interestRate!,
        loan_term_months: Number.parseInt(formData.loanTermMonths, 10),
        loan_purpose: formData.loanPurpose,
        loan_type: formData.loanType,
        loan_product: formData.loanProduct,
        loan_id: formData.loanId || undefined,
        mic: formData.mic || undefined,
        monthly_principal_and_interest: monthlyPrincipalAndInterest,
        prepayment_penalty: formData.prepaymentPenalty,
        prepayment_penalty_details: formData.prepaymentPenalty
          ? formData.prepaymentPenaltyDetails
          : undefined,
        balloon_payment: formData.balloonPayment,
        balloon_payment_amount: formData.balloonPayment
          ? (formData.balloonPaymentAmount ?? undefined)
          : undefined,
        balloon_payment_due_date: formData.balloonPayment
          ? formData.balloonPaymentDueDate || undefined
          : undefined,
        can_increase_after_closing: formData.canIncreaseAfterClosing,
        first_change_date: formData.canIncreaseAfterClosing
          ? formData.firstChangeDate || undefined
          : undefined,
        change_frequency: formData.canIncreaseAfterClosing
          ? formData.changeFrequency || undefined
          : undefined,
        max_rate_increase: formData.canIncreaseAfterClosing
          ? (formData.maxRateIncrease ?? undefined)
          : undefined,
        max_rate_decrease: formData.canIncreaseAfterClosing
          ? (formData.maxRateDecrease ?? undefined)
          : undefined,
        rate_adjustment_cap: formData.canIncreaseAfterClosing
          ? (formData.rateAdjustmentCap ?? undefined)
          : undefined,
        has_escrow: formData.hasEscrow,
        escrowed_items: formData.hasEscrow ? formData.escrowedItems : undefined,
        estimated_escrow_payment: formData.hasEscrow
          ? (formData.estimatedEscrowPayment ?? undefined)
          : undefined,
        estimated_total_monthly_payment: estimatedTotalMonthlyPayment,
      };

      onNext(data);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Loan Information */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b pb-2">
          Basic Loan Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CurrencyInput
            label="Loan Amount"
            value={formData.loanAmount}
            onChange={(v) => updateField('loanAmount', v)}
            error={errors.loanAmount}
            required
            min={0}
            placeholder="360,000.00"
          />

          <PercentageInput
            label="Interest Rate"
            value={formData.interestRate}
            onChange={(v) => updateField('interestRate', v)}
            error={errors.interestRate}
            required
            min={0}
            max={30}
            decimals={3}
            placeholder="6.500"
          />

          <div className="space-y-1.5">
            <Label htmlFor="loanTermMonths">
              Loan Term <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.loanTermMonths}
              onValueChange={(v) => updateField('loanTermMonths', v)}
            >
              <SelectTrigger
                id="loanTermMonths"
                className={cn(errors.loanTermMonths && 'border-destructive')}
              >
                <SelectValue placeholder="Select term" />
              </SelectTrigger>
              <SelectContent>
                {LOAN_TERM_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.loanTermMonths && (
              <p className="text-sm text-destructive">{errors.loanTermMonths}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="loanPurpose">
              Loan Purpose <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.loanPurpose}
              onValueChange={(v) => updateField('loanPurpose', v as LoanPurpose)}
            >
              <SelectTrigger id="loanPurpose">
                <SelectValue placeholder="Select purpose" />
              </SelectTrigger>
              <SelectContent>
                {LOAN_PURPOSE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="loanType">
              Loan Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.loanType}
              onValueChange={(v) => updateField('loanType', v as LoanType)}
            >
              <SelectTrigger id="loanType">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {LOAN_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="loanProduct">
              Loan Product <span className="text-destructive">*</span>
            </Label>
            <Input
              id="loanProduct"
              type="text"
              value={formData.loanProduct}
              onChange={(e) => updateField('loanProduct', e.target.value)}
              placeholder="Fixed Rate"
              list="loan-products"
              className={cn(errors.loanProduct && 'border-destructive')}
            />
            <datalist id="loan-products">
              {LOAN_PRODUCT_SUGGESTIONS.map((product) => (
                <option key={product} value={product} />
              ))}
            </datalist>
            {errors.loanProduct && <p className="text-sm text-destructive">{errors.loanProduct}</p>}
          </div>
        </div>

        {/* Optional identifiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="loanId">Loan ID (Optional)</Label>
            <Input
              id="loanId"
              type="text"
              value={formData.loanId}
              onChange={(e) => updateField('loanId', e.target.value)}
              placeholder="LN-2025-001234"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mic">MIC # (Optional)</Label>
            <Input
              id="mic"
              type="text"
              value={formData.mic}
              onChange={(e) => updateField('mic', e.target.value)}
              placeholder="MIC number"
            />
          </div>
        </div>
      </section>

      {/* Prepayment Penalty */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b pb-2">Prepayment Penalty</h3>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="prepaymentPenalty"
            checked={formData.prepaymentPenalty}
            onCheckedChange={(checked) => updateField('prepaymentPenalty', !!checked)}
          />
          <Label htmlFor="prepaymentPenalty" className="font-normal cursor-pointer">
            This loan has a prepayment penalty
          </Label>
        </div>

        {formData.prepaymentPenalty && (
          <div className="space-y-1.5 pl-6">
            <Label htmlFor="prepaymentPenaltyDetails">Penalty Details</Label>
            <textarea
              id="prepaymentPenaltyDetails"
              value={formData.prepaymentPenaltyDetails}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                updateField('prepaymentPenaltyDetails', e.target.value)
              }
              placeholder="Describe the prepayment penalty terms (e.g., 2% of balance if paid within 2 years)"
              rows={3}
              className={cn(
                'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                errors.prepaymentPenaltyDetails && 'border-destructive',
              )}
            />
            {errors.prepaymentPenaltyDetails && (
              <p className="text-sm text-destructive">{errors.prepaymentPenaltyDetails}</p>
            )}
          </div>
        )}
      </section>

      {/* Balloon Payment */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b pb-2">Balloon Payment</h3>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="balloonPayment"
            checked={formData.balloonPayment}
            onCheckedChange={(checked) => updateField('balloonPayment', !!checked)}
          />
          <Label htmlFor="balloonPayment" className="font-normal cursor-pointer">
            This loan has a balloon payment
          </Label>
        </div>

        {formData.balloonPayment && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6">
            <CurrencyInput
              label="Balloon Payment Amount"
              value={formData.balloonPaymentAmount}
              onChange={(v) => updateField('balloonPaymentAmount', v)}
              error={errors.balloonPaymentAmount}
              required
              min={0}
            />

            <div className="space-y-1.5">
              <Label htmlFor="balloonPaymentDueDate">
                Due Date <span className="text-destructive">*</span>
              </Label>
              <Popover open={balloonDueDateOpen} onOpenChange={setBalloonDueDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="balloonPaymentDueDate"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.balloonPaymentDueDate && 'text-muted-foreground',
                      errors.balloonPaymentDueDate && 'border-destructive',
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.balloonPaymentDueDate
                      ? format(
                          parse(formData.balloonPaymentDueDate, 'yyyy-MM-dd', new Date()),
                          'MMMM d, yyyy',
                        )
                      : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    captionLayout="dropdown"
                    fromYear={new Date().getFullYear()}
                    toYear={new Date().getFullYear() + 40}
                    selected={
                      formData.balloonPaymentDueDate
                        ? parse(formData.balloonPaymentDueDate, 'yyyy-MM-dd', new Date())
                        : undefined
                    }
                    onSelect={(date) => {
                      updateField('balloonPaymentDueDate', date ? format(date, 'yyyy-MM-dd') : '');
                      setBalloonDueDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              {errors.balloonPaymentDueDate && (
                <p className="text-sm text-destructive">{errors.balloonPaymentDueDate}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Rate Adjustments (ARM) */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b pb-2">
          Interest Rate Adjustments
        </h3>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="canIncreaseAfterClosing"
            checked={formData.canIncreaseAfterClosing}
            onCheckedChange={(checked) => updateField('canIncreaseAfterClosing', !!checked)}
          />
          <Label htmlFor="canIncreaseAfterClosing" className="font-normal cursor-pointer">
            Interest rate can increase after closing (Adjustable Rate Mortgage)
          </Label>
        </div>

        {formData.canIncreaseAfterClosing && (
          <div className="space-y-6 pl-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <Label htmlFor="firstChangeDate">
                  First Adjustment Date <span className="text-destructive">*</span>
                </Label>
                <Popover open={firstChangeDateOpen} onOpenChange={setFirstChangeDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="firstChangeDate"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.firstChangeDate && 'text-muted-foreground',
                        errors.firstChangeDate && 'border-destructive',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.firstChangeDate
                        ? format(
                            parse(formData.firstChangeDate, 'yyyy-MM-dd', new Date()),
                            'MMMM d, yyyy',
                          )
                        : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      fromYear={new Date().getFullYear()}
                      toYear={new Date().getFullYear() + 40}
                      selected={
                        formData.firstChangeDate
                          ? parse(formData.firstChangeDate, 'yyyy-MM-dd', new Date())
                          : undefined
                      }
                      onSelect={(date) => {
                        updateField('firstChangeDate', date ? format(date, 'yyyy-MM-dd') : '');
                        setFirstChangeDateOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {errors.firstChangeDate && (
                  <p className="text-sm text-destructive">{errors.firstChangeDate}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="changeFrequency">
                  Adjustment Frequency <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="changeFrequency"
                  type="text"
                  value={formData.changeFrequency}
                  onChange={(e) => updateField('changeFrequency', e.target.value)}
                  placeholder="Every 12 months"
                  className={cn(errors.changeFrequency && 'border-destructive')}
                />
                {errors.changeFrequency && (
                  <p className="text-sm text-destructive">{errors.changeFrequency}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <PercentageInput
                label="Max Rate Increase (per adjustment)"
                value={formData.rateAdjustmentCap}
                onChange={(v) => updateField('rateAdjustmentCap', v)}
                min={0}
                max={10}
                decimals={2}
                placeholder="2.00"
              />

              <PercentageInput
                label="Max Lifetime Rate Increase"
                value={formData.maxRateIncrease}
                onChange={(v) => updateField('maxRateIncrease', v)}
                min={0}
                max={20}
                decimals={2}
                placeholder="6.00"
              />

              <PercentageInput
                label="Max Lifetime Rate Decrease"
                value={formData.maxRateDecrease}
                onChange={(v) => updateField('maxRateDecrease', v)}
                min={0}
                max={20}
                decimals={2}
                placeholder="3.00"
              />
            </div>
          </div>
        )}
      </section>

      {/* Escrow */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground border-b pb-2">Escrow Account</h3>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="hasEscrow"
            checked={formData.hasEscrow}
            onCheckedChange={(checked) => updateField('hasEscrow', !!checked)}
          />
          <Label htmlFor="hasEscrow" className="font-normal cursor-pointer">
            This loan will have an escrow account
          </Label>
        </div>

        {formData.hasEscrow && (
          <div className="space-y-4 pl-6">
            <div className="space-y-2">
              <Label>
                Escrowed Items <span className="text-destructive">*</span>
              </Label>
              <div className="space-y-2">
                {ESCROW_ITEM_OPTIONS.map((item) => (
                  <div key={item.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`escrow-${item.value}`}
                      checked={formData.escrowedItems.includes(item.value)}
                      onCheckedChange={() => toggleEscrowItem(item.value)}
                    />
                    <Label htmlFor={`escrow-${item.value}`} className="font-normal cursor-pointer">
                      {item.label}
                    </Label>
                  </div>
                ))}
              </div>
              {errors.escrowedItems && (
                <p className="text-sm text-destructive">{errors.escrowedItems}</p>
              )}
            </div>

            <div className="max-w-xs">
              <CurrencyInput
                label="Estimated Monthly Escrow Payment"
                value={formData.estimatedEscrowPayment}
                onChange={(v) => updateField('estimatedEscrowPayment', v)}
                min={0}
                placeholder="500.00"
              />
            </div>
          </div>
        )}
      </section>

      {/* Loan Summary (Read-Only) */}
      <section className="space-y-4 bg-muted/50 dark:bg-muted/20 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Loan Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-background p-4 rounded border">
            <p className="text-sm text-muted-foreground">Total Loan Amount</p>
            <p className="text-2xl font-bold">{formatCurrency(formData.loanAmount || 0)}</p>
          </div>

          <div className="bg-background p-4 rounded border">
            <p className="text-sm text-muted-foreground">Monthly P&I</p>
            <p className="text-2xl font-bold">{formatCurrency(monthlyPrincipalAndInterest)}</p>
          </div>

          <div className="bg-background p-4 rounded border">
            <p className="text-sm text-muted-foreground">Est. Monthly Total</p>
            <p className="text-2xl font-bold">{formatCurrency(estimatedTotalMonthlyPayment)}</p>
            {formData.hasEscrow && formData.estimatedEscrowPayment && (
              <p className="text-xs text-muted-foreground mt-1">
                Includes {formatCurrency(formData.estimatedEscrowPayment)} escrow
              </p>
            )}
          </div>

          <div className="p-4 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
            <p className="text-sm text-blue-600 dark:text-blue-400">Total Payable</p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {(() => {
                const totalMonths = Number.parseInt(formData.loanTermMonths || '0', 10);
                const totalMonthlyPayments = estimatedTotalMonthlyPayment * totalMonths;

                // For balloon loans, add balloon payment to total
                if (formData.balloonPayment && formData.balloonPaymentAmount) {
                  return formatCurrency(totalMonthlyPayments + formData.balloonPaymentAmount);
                }

                return formatCurrency(totalMonthlyPayments);
              })()}
            </p>
            {formData.balloonPayment && formData.balloonPaymentAmount && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Includes {formatCurrency(formData.balloonPaymentAmount)} balloon
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          * Total payable includes principal, interest
          {formData.hasEscrow && formData.estimatedEscrowPayment ? ', escrow' : ''}
          {formData.balloonPayment && formData.balloonPaymentAmount ? ', and balloon payment' : ''}{' '}
          over the loan term
        </p>
      </section>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit">Next: Parties</Button>
      </div>
    </form>
  );
};

export default LoanTermsForm;
